import React, { useEffect, useRef, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { ArrowLeft, Check } from 'lucide-react';
import logo from '../checkout/dr-shumard-logo.png';
import frame from '../auth/welcome.module.css';
import s from './handoff.module.css';

// The bar's run, as on the patient's brief welcome: the handoff waits for it even when the token is quicker.
const READY_MS = 1600;
const LEAVE_MS = 400;

// Opening Learn / Vienna — the patient welcome's frame and sequence (pages/auth/WelcomeScreen) in team copy.
// `mint` returns a promise of the SSO token; `target(token)` is the URL that redeems it.
export default function AppHandoff({ app, mint, target }) {
  const { profile } = useOutletContext();
  const [token, setToken] = useState(null);
  const [paced, setPaced] = useState(false);
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);
  const launched = useRef(-1);

  // Each attempt mints a FRESH token — a failed handoff burns its single-use token, so retrying the old URL can
  // never work. StrictMode double-runs effects in dev; two parallel handoffs make Learn's Supabase invalidate the
  // first magic-link hash — exactly one launch per attempt.
  useEffect(() => {
    if (launched.current === attempt) return;
    launched.current = attempt;
    setError('');
    setToken(null);
    Promise.resolve()
      .then(mint)
      .then(setToken)
      .catch((e) => setError(e?.response?.data?.detail || e?.message || `Could not open ${app.label}.`));
  }, [attempt, app.label, mint]);

  // The bar's pace runs on its own: tied to the launch above, StrictMode's cleanup would clear it and the
  // guarded re-run would never restart it — the page would sit on "Signing you in…".
  useEffect(() => {
    setPaced(false);
    const pace = window.setTimeout(() => setPaced(true), READY_MS);
    return () => window.clearTimeout(pace);
  }, [attempt]);

  const ready = Boolean(token) && paced && !error;

  // replace(): Back must land on the workspace, not on a burned token URL that would relaunch the handoff.
  useEffect(() => {
    if (!ready) return undefined;
    const leave = window.setTimeout(() => window.location.replace(target(token)), LEAVE_MS);
    return () => window.clearTimeout(leave);
  }, [ready, token, target]);

  const firstName = (profile?.name || '').trim().split(/\s+/)[0];
  const message = error || (ready ? `You’re signed in. Taking you to ${app.label}…`
    : token ? 'Signing you in…' : `Opening ${app.label}…`);

  return (
    <div className={`${frame.page} ${s.page}`}>
      <header className={frame.header}>
        <Link to="/staff" aria-label="Team workspace home">
          <img src={logo} alt="Dr. Shumard" width={1024} height={152} className={frame.logo} />
        </Link>
        <span className={frame.portalLabel}>Team workspace</span>
      </header>

      <main className={frame.main}>
        <section className={frame.welcome} aria-labelledby="handoff-title">
          <div className={s.app}>
            <span className={s.appIcon} data-tone={app.tone}><app.icon size={30} strokeWidth={1.5} aria-hidden="true" /></span>
            <p>{app.label}</p>
          </div>

          <h1 id="handoff-title">{firstName ? <>Welcome back,<br /><span>{firstName}.</span></> : <>Hello there,<br /><span>welcome back!</span></>}</h1>
          <p className={frame.copy}>{app.blurb}</p>

          <div className={frame.setup} data-ready={ready} data-brief="true" data-error={error ? 'true' : undefined}>
            <div className={frame.track} aria-hidden="true">
              <span key={attempt} className={frame.fill} />
            </div>
            <div className={frame.status} role="status" aria-live="polite" aria-atomic="true">
              <span className={frame.statusMark} aria-hidden="true">
                {ready ? <Check size={15} strokeWidth={2} /> : <span className={frame.statusDot} />}
              </span>
              <span key={message} className={frame.statusText}>{message}</span>
            </div>
          </div>

          <div className={frame.actionSpace}>
            {error ? (
              <button type="button" className={frame.quietButton} onClick={() => setAttempt((n) => n + 1)}>Try again</button>
            ) : (
              <p className={frame.waiting}>Just a moment. We’ll take it from here.</p>
            )}
          </div>
        </section>
      </main>

      <footer className={frame.footer}>
        <span>Dr. Shumard · Team workspace</span>
        <Link to="/staff"><ArrowLeft size={13} aria-hidden="true" /> Back to workspace</Link>
      </footer>
    </div>
  );
}
